import { useState } from "react";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { Ingredient, Step, CuisineType, FoodType, MeasurementUnit, NutritionalValues } from "@/types/recipe";
import { RecipeForm } from "@/components/recipe/RecipeForm";
import { useNavigate } from "react-router-dom";
import NutritionalInfo from "@/components/recipe/NutritionalInfo";
import type { Json } from "@/integrations/supabase/types";

interface RecipeFormContainerProps {
  id: string;
  initialData: {
    title: string;
    description: string;
    ingredients: Ingredient[];
    steps: Step[];
    imageUrl: string | null;
    visibility: "public" | "subscription";
    cuisineType: CuisineType | '';
    foodType: FoodType | '';
    servings: number;
    nutritionalValues: NutritionalValues | null;
  };
}

export const RecipeFormContainer = ({ id, initialData }: RecipeFormContainerProps) => {
  const navigate = useNavigate();
  const [title, setTitle] = useState(initialData.title);
  const [description, setDescription] = useState(initialData.description);
  const [ingredients, setIngredients] = useState<Ingredient[]>(initialData.ingredients);
  const [steps, setSteps] = useState<Step[]>(initialData.steps);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(initialData.imageUrl);
  const [visibility, setVisibility] = useState<"public" | "subscription">(initialData.visibility);
  const [cuisineType, setCuisineType] = useState<CuisineType | ''>(initialData.cuisineType);
  const [foodType, setFoodType] = useState<FoodType | ''>(initialData.foodType);
  const [nutritionalValues, setNutritionalValues] = useState<NutritionalValues | null>(initialData.nutritionalValues);
  const [servings, setServings] = useState(initialData.servings || 1);

  const uploadImage = async (file: File): Promise<string | null> => {
    const fileExt = file.name.split('.').pop();
    const filePath = `${crypto.randomUUID()}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from('recipe-images')
      .upload(filePath, file);

    if (uploadError) {
      throw uploadError;
    }

    const { data: { publicUrl } } = supabase.storage
      .from('recipe-images')
      .getPublicUrl(filePath);

    return publicUrl;
  };

  const calculateNutritionalValues = async () => {
    try {
      if (ingredients.some(i => !i.name || i.amount <= 0)) {
        toast.error("Please fill in all ingredients with valid amounts");
        return;
      }

      const { data, error } = await supabase.functions.invoke('calculate-nutrition', {
        body: { ingredients }
      });

      if (error) {
        console.error('Error calculating nutrition:', error);
        toast.error('Failed to calculate nutritional values');
        return;
      }

      setNutritionalValues(data);
      toast.success('Nutritional values calculated!');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to calculate nutritional values');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!id) {
      toast.error("No recipe ID provided");
      return;
    }

    if (!nutritionalValues) {
      toast.error("Please calculate nutritional values before publishing");
      return;
    }

    setIsSubmitting(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error("Please sign in to edit this recipe");
        return;
      }

      let imageUrl = imagePreview;
      if (imageFile) {
        imageUrl = await uploadImage(imageFile);
      }

      const ingredientsJson = ingredients.map(ing => ({
        amount: Number(ing.amount),
        unit: ing.unit,
        name: ing.name.trim()
      }));

      const stepsJson = steps.map(step => ({
        instruction: step.instruction.trim(),
        temperature: step.temperature || null,
        time: step.time || null
      }));

      const recipeData = {
        title: title.trim(),
        description: description.trim() || null,
        ingredients: ingredientsJson,
        steps: stepsJson,
        image_url: imageUrl,
        visibility,
        cuisine_type: cuisineType || null,
        food_type: foodType || null,
        servings: servings,
        nutritional_values: nutritionalValues as unknown as Json
      };

      console.log("Updating recipe:", id, "with data:", recipeData);

      const { error } = await supabase
        .from('recipes')
        .update(recipeData)
        .eq('id', id);

      if (error) {
        console.error('Error updating recipe:', error);
        throw error;
      }

      toast.success("Recipe updated successfully!");
      navigate(`/dashboard`);
    } catch (error: any) {
      console.error('Error updating recipe:', error);
      toast.error(error.message || "Failed to update recipe");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <RecipeForm
      title={title}
      setTitle={setTitle}
      description={description}
      setDescription={setDescription}
      ingredients={ingredients}
      setIngredients={setIngredients}
      steps={steps}
      setSteps={setSteps}
      visibility={visibility}
      setVisibility={setVisibility}
      imageFile={imageFile}
      setImageFile={setImageFile}
      imagePreview={imagePreview}
      setImagePreview={setImagePreview}
      cuisineType={cuisineType}
      setCuisineType={setCuisineType}
      foodType={foodType}
      setFoodType={setFoodType}
      servings={servings}
      setServings={setServings}
      onPreview={() => {}}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      onCalculateNutrition={calculateNutritionalValues}
      hasCalculatedNutrition={!!nutritionalValues}
      nutritionalValues={nutritionalValues}
      onNutritionalValuesEdit={setNutritionalValues}
    />
  );
};
